import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { History, Bookmark, Sparkles, Trash2, Loader2, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { API } from "@/lib/config";
import { useCountry } from "@/components/contexts/CountryContext";
import { usePlayer } from "@/components/contexts/PlayerContext";
import { useUser } from "@/components/contexts/UserContext";
import { CompactGrid } from "@/components/VideoCardCompact";
import { useStoredList, clearHistory, getTasteProfile } from "@/lib/storage";

/**
 * Watch history, saved videos, and a "For You" feed.
 *
 * All three are built from localStorage, so none of it needs accounts or a login.
 * The trade-off is that it does not follow the visitor to another device.
 */
export const LibraryPage = () => {
  const [tab, setTab] = useState("history");
  const [history] = useStoredList("history");
  const [saved] = useStoredList("saved");
  const [follows] = useStoredList("follows");
  const { play } = usePlayer();

  const tabs = [
    { id: "history", label: "History", icon: History, count: history.length },
    { id: "saved", label: "Saved", icon: Bookmark, count: saved.length },
    { id: "following", label: "Following", icon: Users, count: follows.length },
    { id: "foryou", label: "For You", icon: Sparkles, count: null },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" data-testid="library-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="font-heading text-2xl sm:text-3xl font-black mb-6">Your Library</h1>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          {tabs.map(({ id, label, icon: Icon, count }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 px-4 min-h-[44px] rounded-xl text-sm font-medium transition-colors ${
                tab === id ? "bg-orange-500 text-black" : "bg-white/5 border border-white/10 hover:bg-white/10"
              }`}
              data-testid={`library-tab-${id}`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {count !== null && count > 0 && (
                <span className={`text-[10px] ${tab === id ? "text-black/60" : "text-white/40"}`}>{count}</span>
              )}
            </button>
          ))}

          {tab === "history" && history.length > 0 && (
            <button
              onClick={() => window.confirm("Clear your watch history?") && clearHistory()}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-white/50 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear history
            </button>
          )}
        </div>

        {tab === "history" && (
          <CompactGrid videos={history} onPlay={play} emptyMessage="Nothing watched yet. Play a video and it shows up here." />
        )}
        {tab === "saved" && (
          <CompactGrid videos={saved} onPlay={play} emptyMessage="No saved videos yet. Tap Save on any video." />
        )}
        {tab === "following" && <FollowingList channels={follows} />}
        {tab === "foryou" && <ForYouFeed onPlay={play} />}
      </div>
    </div>
  );
};

/** Channels being followed, each linking through to its own page. */
const FollowingList = ({ channels }) => {
  if (!channels.length) {
    return (
      <p className="text-center text-sm text-white/40 py-16">
        Not following anyone yet. Tap Follow on any channel.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {channels.map((channel) => (
        <Link
          key={channel.channel_id}
          to={`/channel/${channel.channel_id}`}
          className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-orange-500/40 transition-colors group"
        >
          {channel.channel_avatar ? (
            <img src={channel.channel_avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center text-black font-bold">
              {channel.channel_title?.charAt(0)?.toUpperCase() || "C"}
            </div>
          )}
          <span className="text-sm font-medium truncate group-hover:text-orange-400 transition-colors">
            {channel.channel_title || channel.channel_id}
          </span>
        </Link>
      ))}
    </div>
  );
};

/**
 * Recommendations from followed channels plus the categories already watched.
 *
 * The taste profile is computed on-device and sent up as query parameters, so this
 * works signed out. Signed in, the server uses the stored profile instead and the
 * parameters are ignored - same endpoint either way.
 */
const ForYouFeed = ({ onPlay }) => {
  const { country } = useCountry();
  const { authHeaders } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [follows] = useStoredList("follows");
  const profile = useMemo(() => getTasteProfile(), []);

  const followIds = follows.map((c) => c.channel_id).join(",");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/foryou`, {
          params: {
            country: country.code,
            categories: profile.categories.join(","),
            languages: profile.languages.join(","),
            channels: followIds,
            limit: 24,
          },
          headers: authHeaders,
        });
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setData({ videos: [], notice: "Could not load recommendations." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [country.code, profile, followIds, authHeaders]);

  if (profile.watched === 0 && follows.length === 0) {
    return (
      <p className="text-center text-sm text-white/40 py-16">
        Watch a few videos or follow a channel, and recommendations appear here.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
      </div>
    );
  }

  const basis = [
    follows.length > 0 && `${follows.length} channel${follows.length === 1 ? "" : "s"} you follow`,
    profile.watched > 0 && `${profile.watched} video${profile.watched === 1 ? "" : "s"} you've watched`,
  ].filter(Boolean);

  return (
    <>
      {basis.length > 0 && (
        <p className="text-xs text-white/40 mb-4">Based on {basis.join(" and ")}.</p>
      )}
      {data?.notice && <p className="text-xs text-white/40 mb-4">{data.notice}</p>}
      <CompactGrid
        videos={data?.videos || []}
        onPlay={onPlay}
        linkTo={(v) => `/video/${v.id}`}
        emptyMessage="Nothing to recommend yet."
      />
    </>
  );
};
